"use client";
/**
 * The article archive — every post, searchable.
 *
 * Mirrors PodcastArchive: same chapters.css markup, and EVERY row is rendered
 * into the DOM with pagination only hiding rows outside the current window, so
 * all posts carry a crawlable link. See .media-row[hidden] in chapters.css.
 */
import { useMemo, useState } from "react";
import Link from "next/link";

export type PostEntry = {
    path: string;
    title: string;
    date: string;
    author?: string;
};

const PAGE_SIZE = 12;

const SearchIcon = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
);
const ArrowUpRight = ({ size = 16, className }: { size?: number; className?: string }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M7 7h10v10" /></svg>
);

export default function BlogArchive({ entries }: { entries: PostEntry[] }) {
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    const result = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter(
            (e) =>
                e.title.toLowerCase().includes(q) ||
                (e.author ?? "").toLowerCase().includes(q),
        );
    }, [entries, query]);

    const pages = Math.ceil(result.length / PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;

    return (
        <section className="media-library section-pad" id="articles">
            <div className="section-label mono">
                FIELD NOTES / {entries.length} ARTICLES
            </div>
            <div className="directory-controls">
                <label className="search-field">
                    <SearchIcon size={18} />
                    <input
                        aria-label="Search articles by title or author"
                        placeholder="Search a topic or author"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                    />
                </label>
            </div>
            <div aria-live="polite" className="results-count mono">
                {result.length} RESULTS
            </div>
            <div className="media-list">
                {result.map((item, i) => (
                    <Link
                        className="media-row"
                        hidden={i < start || i >= start + PAGE_SIZE}
                        style={{ animationDelay: `${(i % PAGE_SIZE) * 25}ms` }}
                        href={item.path}
                        key={item.path}
                    >
                        <span className="media-row-index" aria-hidden="true">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="media-row-type mono">
                            <ArrowUpRight size={16} /> FIELD NOTES
                        </span>
                        <h3>{item.title}</h3>
                        <span className="media-row-date mono">
                            {item.author || item.date || "AGENT BOOST"}
                        </span>
                        <ArrowUpRight className="row-arrow" />
                    </Link>
                ))}
            </div>
            {result.length === 0 && (
                <div className="empty-state">
                    <h3>No matches yet.</h3>
                    <p>Try a broader phrase or an author name.</p>
                    <button type="button" className="button" onClick={() => setQuery("")}>
                        Show every article
                    </button>
                </div>
            )}
            {pages > 1 && (
                <nav className="media-pagination" aria-label="Archive pages">
                    <button
                        type="button"
                        className="button"
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        Previous
                    </button>
                    <span className="mono">
                        PAGE {page} / {pages}
                    </span>
                    <button
                        type="button"
                        className="button"
                        disabled={page === pages}
                        onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    >
                        Next
                    </button>
                </nav>
            )}
        </section>
    );
}
